import { RoutesMap, Options, connectRoutes, Meta, NOT_FOUND, redirect, Action } from 'redux-first-router'
import { doLoadChannel } from './actions/channels';
import queryString from 'query-string'
import { doRouteSync } from './actions/location';
import { ClusterMessagesClient } from './store';
import { RootState } from './reducers/persistedReducer';
import { selectChannelSettings, selectActiveChannel, selectChannelById, selectLoggedIn, selectChannelCellExists, selectIsMobile } from './selectors';
import Log from '../utils/Log';
import { randomString } from '../utils/utils'
import { doSetOauthNonce, doSetDeferredDestination, doAcceptTerms, doSetMobileMenuOpen } from './actions/system';
import { doAuthUser, doSetAuthSourceToken, USER_ACTIONS, doLogoutUser, doPostAuthUser } from './actions/user';
import api from '../lib/app-api';
import config from '../config.json'
import { doOpenModal, doCloseModal } from './actions/modal';
import { ModalType } from '../Modal/ModalTypes';
import firebase from '../lib/firebase';
import { shallowEqual } from 'react-redux';
import { doPanToCell } from './actions/channelLayout';

export enum ROUTE_ACTIONS {
  HOME = "HOME",
  CHANNEL = "CHANNEL",
  LOGIN = "LOGIN",
  AUTH_RBN = "AUTH_RBN",
  AUTH_RBN_CALLBACK = "AUTH_RBN_CALLBACK",
  AUTH_GUEST = "AUTH_GUEST"
}

export const routesMap: RoutesMap<{ anonymous?: boolean }> = {
  [ROUTE_ACTIONS.HOME]: '/',
  [ROUTE_ACTIONS.CHANNEL]: {
    path: '/channel/:id',
    thunk: (dispatch, getState) => {
      // Load channel data when a channel route is hit.
      const state = getState() as RootState;
      const { payload, query } = state.location;

      // Redirect to not found if the channel isn't in the list of available channels.
      if (typeof selectChannelById(state, payload?.id) === "undefined") {
        return dispatch(redirect({type: NOT_FOUND}));
      }

      const { type: prevType, query: prevQuery } = state.location.prev;
      const { channelId: prevLayoutChannelId, centroidId: prevLayoutCentroid, familyTag: prevFamilyTag, filters: prevFilters } = selectActiveChannel(state);

      // Parse incoming filters
      let currentFilters;
      try {
        currentFilters = JSON.parse(query?.filters??"{}");
      } catch {
        currentFilters = {};
      }

      // Get pan to cell
      let panToCell: number | false = false;
      let prevPanToCell: number | false = false;
      const pan = parseInt(query?.pan??"");
      const prevPan = parseInt(prevQuery?.pan??"");
      if (!isNaN(pan)) {
        panToCell = pan;
      }
      if (!isNaN(prevPan)) {
        prevPanToCell = prevPan;
      }

      // Don't reload channel info if channel id, centroidid, familytag, and filters have not changed
      if ((prevType === '' || prevType === ROUTE_ACTIONS.CHANNEL)
        && payload?.id === prevLayoutChannelId
        && (parseInt(query?.centroidId??"0")||undefined) === prevLayoutCentroid
        && query?.familyTag === prevFamilyTag
        && shallowEqual(currentFilters, prevFilters)) {
          // We have some special cases if there's a cell pan query
          if (panToCell) {
            // If the prev and current pan queries are different, do some more checking
            // otherwise, return
            if (panToCell !== prevPanToCell) {
              // If the cell exists in the channel, go ahead and pan to it and don't reload.
              if (selectChannelCellExists(state, panToCell)) {
                dispatch(doPanToCell(panToCell));
                return;
              }
              //
              // Don't return/escape. The cell we're panning to doesn't exist. Reload the channel.
              //
            } else {
              return;
            }
          } else {
            return;
          }
      }

      // Load the channel
      dispatch(doLoadChannel(payload.id, (query?.centroidId ? parseInt(query?.centroidId) : undefined), query?.familyTag, panToCell, currentFilters));

      // Unsub/Sub from the channel if the channel ID has changed from the last route
      if (!(prevType === ROUTE_ACTIONS.CHANNEL && payload.id === prevLayoutChannelId)) {
        if (prevLayoutChannelId) {
          ClusterMessagesClient.unsubscribe("notifications", prevLayoutChannelId);
        }

        ClusterMessagesClient.subscribe("notifications", payload.id);
      }
    },
    coerceNumbers: true
  },
  [ROUTE_ACTIONS.LOGIN]: {
    path: '/login',
    anonymous: true
  },
  [ROUTE_ACTIONS.AUTH_RBN]: {
    path: '/auth/rbn',
    anonymous: true
  },
  [ROUTE_ACTIONS.AUTH_RBN_CALLBACK]: {
    path: '/auth/rbn/callback',
    anonymous: true
  },
  [ROUTE_ACTIONS.AUTH_GUEST]: {
    path: '/auth/guest',
    anonymous: true
  },
}

const options: Options = {
  onBeforeChange: (dispatch, getState, action) => {
    const state = getState();

    if (action?.action?.type === NOT_FOUND) {
      return;
    }

    const routeIsAnonymous = (routesMap[action?.action?.type] as typeof routesMap)?.anonymous??false;
    const loggedIn = selectLoggedIn(state);

    const query = (action.action?.meta as Meta)?.query??action.action?.query;

    //
    // Redirect privilaged routes to login.
    //
    if (!loggedIn && !routeIsAnonymous) {
      dispatch(doSetDeferredDestination(action.action as Action));

      // If coming from RBN's website, route to rbn auth page to automatically log them in.
      // @TODO figure this out
      // if (document.referrer.includes("rbnenergy.com")) {
      //   return dispatch(doRouteSync(state, ROUTE_ACTIONS.AUTH_RBN, {}, true));
      // } else {
      //   return dispatch(doRouteSync(state, ROUTE_ACTIONS.LOGIN, {}, true));
      // }

      return dispatch(doRouteSync(state, ROUTE_ACTIONS.LOGIN, {}, true));
    }

    //
    // Redirect to RBN OAuth
    //
    else if (action.action.type === ROUTE_ACTIONS.AUTH_RBN) {
      const oauthNonce = randomString(20);
      dispatch(doSetOauthNonce(oauthNonce));

      // Redirect to RBN Authorize
      window.location.href = api.getAuthSourceCodeRedirect(oauthNonce);
    }

    //
    // Log in with Guest Token
    //
    else if (action.action.type === ROUTE_ACTIONS.AUTH_GUEST) {
      return ((dispatch(doAuthUser()) as unknown) as Promise<unknown>).then(() => {
        dispatch({
          type: USER_ACTIONS.TOKEN_REFRESH_UNLOCKED
        });

        return dispatch(doPostAuthUser());
      }).catch(error => {
        // @TODO figure out what to do with this eventuality.
        console.log(error);
      });
    }

    //
    // Handle auth codes
    //
    else if (action.action.type === ROUTE_ACTIONS.AUTH_RBN_CALLBACK) {
      const {pathname, search} = (action.action?.meta as Meta).location.current;
      const uri = pathname + "?" + search;

      // Authenticate with Cluster API.
      return api.requestAuthSourceToken(uri, state.system.oauthNonce).then((authToken) => {
        const { accessToken, refreshToken, data, tokenType } = authToken;
        dispatch(doSetAuthSourceToken({ accessToken, refreshToken, data, tokenType }));
        dispatch({
          type: USER_ACTIONS.TOKEN_REFRESH_LOCKED
        });

        return ((dispatch(doAuthUser(authToken.accessToken)) as unknown) as Promise<unknown>).then(() => {
          dispatch({
            type: USER_ACTIONS.TOKEN_REFRESH_UNLOCKED
          });

          return dispatch(doPostAuthUser());
        }).catch(error => {
          // @TODO figure out what to do with this eventuality.
          console.log(error);
        });
      }).catch(error => {
        console.error(error.message);
        firebase.analytics.logEvent("login_failed", {error: error.message});
        dispatch(doLogoutUser("An unknown error has occured while logging in with RBN."));
      })
    }

    //
    // Redirect to HOME when visiting LOGIN while already logged in.
    //
    else if (loggedIn && action.action.type === ROUTE_ACTIONS.LOGIN) {
      return dispatch(doRouteSync(state, ROUTE_ACTIONS.HOME,{query: {show: 'channels'}}, true));
    }

    //
    // Open channels on the home route if not already open.
    //
    else if (action.action.type === ROUTE_ACTIONS.HOME && (query?.show === undefined || query?.show === 'none')) {
      return dispatch(doRouteSync(state, ROUTE_ACTIONS.HOME,{query: {show: 'channels'}}, true));
    }

    //
    // On channel load when there's no centroid set, check the channel settings
    // to see if we need to reload the last visited centroid/tag combo
    //
    else if (action.action.type === ROUTE_ACTIONS.CHANNEL && !query?.centroidId) {

      const {initialCentroid, lastVisited} = selectChannelSettings(state, action.action.payload!.id);
      if (initialCentroid === "last" && lastVisited?.centroidId) {
        Log.info("Rerouting to last channel centroid and tag", "route");
        dispatch(doRouteSync(state,
          ROUTE_ACTIONS.CHANNEL,
          {
            payload: {id: action.action.payload!.id},
            query: {centroidId: lastVisited?.centroidId?.toString(), familyTag: lastVisited?.familyTag, pan: action.action?.query?.pan},
            keepQuery: ["show"]
          },
          true
        ));
        return false;
      }
    }

    //
    // Open TOS agreement if the current version hasn't been agreed to.
    //
    if (state.system.agreedUponTosVersion !== config.TOS_VERSION) {
      dispatch(doOpenModal({
        type: ModalType.Url,
        url: "/terms.html",
        title: "Terms & Conditions",
        promptButton: {
          title: "I Agree",
          action: () => {
            dispatch(doAcceptTerms());
            dispatch(doCloseModal());
          },
        },
        disableClose: true
      }))
    }

    //
    // Switch to events drawer after channel selection.
    // Checks if the incoming route is the channel and that the channel id changed and that the events are not already shown.
    //
    if (action.action.type === ROUTE_ACTIONS.CHANNEL && state.location.payload?.id !== action.action.payload?.id && query?.show !== "events") {
      if(selectIsMobile(state)) {
        return dispatch(doSetMobileMenuOpen(false));
      } else {
        return dispatch(doRouteSync(state, action.action.type, {payload: action.action.payload, query: {...query, show: "events"}}, true));
      }
    }
  },
  title: undefined,
  initialDispatch: false, // Don't dispatch the route immediately on load. We'll run it after store rehydration.
  querySerializer: queryString
}

export default connectRoutes(routesMap, options);
